import type { InboxEmail, SuggestedAction } from '../types/inbox.types';

export type WorkflowStepId = 'topic' | 'providers' | 'documents' | 'events' | 'pendingItems' | 'final';

export type WorkflowStepItem = {
  id: WorkflowStepId;
  title: string;
  shortTitle: string;
  description: string;
  isCompleted: boolean;
  actionCount?: number;
};

export function getWorkflowSteps(email: InboxEmail | null): WorkflowStepItem[] {
  if (!email) return [];

  const actions = email.parsedActions || [];

  const providerActions = actions.filter(
    (a) => a.type === 'CREATE_PROVIDER' || a.type === 'CREATE_PROVIDER_CONTACT'
  );
  const documentActions = actions.filter((a) => a.type === 'CREATE_DOCUMENT');
  const eventActions = actions.filter((a) => a.type === 'CREATE_EVENT');
  const pendingItemActions = actions.filter((a) => a.type === 'CREATE_PENDING_ITEM');

  const steps: WorkflowStepItem[] = [];

  // Step 1: Topic selection (always included)
  steps.push({
    id: 'topic',
    title: 'Sujet associé',
    shortTitle: 'Sujet',
    description: 'Associer cet email à un dossier pour organiser les ressources',
    isCompleted: Boolean(email.matched_sub_topic_id),
  });

  // Step 2: Providers & contacts (only if detected)
  if (providerActions.length > 0) {
    const isCompleted = providerActions.every((a) => Boolean(a.isCompleted || a.isIgnored));
    steps.push({
      id: 'providers',
      title: 'Prestataire & Contact',
      shortTitle: 'Prestataires',
      description: `${providerActions.length} prestataire${providerActions.length > 1 ? 's' : ''} ou contact${providerActions.length > 1 ? 's' : ''} détecté${providerActions.length > 1 ? 's' : ''}`,
      isCompleted,
      actionCount: providerActions.length,
    });
  }

  // Step 3: Documents & quotes (only if detected)
  if (documentActions.length > 0) {
    const isCompleted = documentActions.every((a) => Boolean(a.isCompleted || a.isIgnored));
    const hasContract = documentActions.some((a) => Boolean(a.isContract));
    const count = documentActions.length;
    const description = hasContract
      ? `${count} contrat${count > 1 ? 's' : ''}, devis ou document${count > 1 ? 's' : ''} détecté${count > 1 ? 's' : ''}`
      : `${count} devis ou document${count > 1 ? 's' : ''} détecté${count > 1 ? 's' : ''}`;

    steps.push({
      id: 'documents',
      title: 'Documents & Devis',
      shortTitle: 'Documents',
      description,
      isCompleted,
      actionCount: documentActions.length,
    });
  }

  // Step 4: Events & appointments (only if detected)
  if (eventActions.length > 0) {
    const isCompleted = eventActions.every((a) => Boolean(a.isCompleted || a.isIgnored));
    steps.push({
      id: 'events',
      title: 'Rendez-vous & Interventions',
      shortTitle: 'RDV',
      description: `${eventActions.length} rendez-vous ou intervention${eventActions.length > 1 ? 's' : ''} détecté${eventActions.length > 1 ? 's' : ''}`,
      isCompleted,
      actionCount: eventActions.length,
    });
  }

  // Step 5: Pending items (only if detected)
  if (pendingItemActions.length > 0) {
    const isCompleted = pendingItemActions.every((a) => Boolean(a.isCompleted || a.isIgnored));
    steps.push({
      id: 'pendingItems',
      title: 'Éléments à suivre',
      shortTitle: 'À suivre',
      description: `${pendingItemActions.length} élément${pendingItemActions.length > 1 ? 's' : ''} à suivre détecté${pendingItemActions.length > 1 ? 's' : ''}`,
      isCompleted,
      actionCount: pendingItemActions.length,
    });
  }

  // Step 6: Finalization (always included)
  steps.push({
    id: 'final',
    title: 'Finalisation',
    shortTitle: 'Finalisation',
    description: 'Ajouter une communication facultative et marquer comme traité',
    isCompleted: email.status === 'processed',
  });

  return steps;
}

export type FinalizationState = 'pendingActions' | 'missingTopic' | 'allIgnored' | 'ready';

/**
 * Actions the user must apply or ignore before closing the e-mail.
 * Saving the e-mail as a communication is optional, so it never blocks the finalization.
 */
export function getRequiredActions(actions: SuggestedAction[]): SuggestedAction[] {
  return actions.filter((action) => action.type !== 'ADD_COMMUNICATION');
}

export function getFinalizationState(email: InboxEmail): FinalizationState {
  const requiredActions = getRequiredActions(email.parsedActions || []);
  const isAllIgnored = requiredActions.length > 0 && requiredActions.every((action) => Boolean(action.isIgnored));

  if (isAllIgnored) return 'allIgnored';
  if (requiredActions.some((action) => !action.isCompleted && !action.isIgnored)) return 'pendingActions';
  if (!email.matched_sub_topic_id) return 'missingTopic';
  return 'ready';
}

export function isWorkflowReadyToComplete(email: InboxEmail | null): boolean {
  if (!email) return false;
  const state = getFinalizationState(email);
  return state === 'ready' || state === 'allIgnored';
}
